import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  computed,
  signal,
  input,
} from '@angular/core';
import dayjs from 'dayjs';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { ReceiptComponent } from '../../user-protected/tax-payment/receipt/receipt.component';
import { BillsService } from '../../../core/services/bills.service';
import { LocalService } from '../../../core/services/local.service';
import { ReceiptData } from '../../../shared/models';
import { ObjectLiteral } from '../../../core/models/ObjectLiteral';
import { ActivatedRoute } from '@angular/router';
import { PaymentGatewayModalComponent } from '../../../shared/payment-gateway-modal/payment-gateway-modal.component';
import { DemandNoticeInvoiceComponent } from '../../tao/demand-notice-invoice/demand-notice-invoice.component';
import { takeUntil } from 'rxjs';
import { Unsub } from '../../../core/services/unsub.class';
import { SpecialInvoiceComponent } from '../../tao/special-invoice/special-invoice.component';

import { ConsolidatedDemandNoticeInvoiceComponent } from '../../tao/consolidated-demand-notice-invoice/consolidated-demand-notice-invoice.component';
@Component({
  selector: 'app-make-payment-tsp',
  standalone: true,
  imports: [
    ConsolidatedDemandNoticeInvoiceComponent,
    CommonModule,
    ReceiptComponent,
    PaymentGatewayModalComponent,
    DemandNoticeInvoiceComponent,
    SpecialInvoiceComponent,
  ],
  templateUrl: './make-payment-tsp.component.html',
  styleUrl: './make-payment-tsp.component.css',
})
export class MakePaymentTspComponent extends Unsub {
  billService = inject(BillsService);
  activatedRoute = inject(ActivatedRoute);

  localService = inject(LocalService);
  redirectQueryparams = injectQueryParams('redirect');
  isAnonymous = injectQueryParams('isAnonymous');
  isAReceipt = computed(() => {
    return this.billQueryResponse.data()?.bill?.payStatusId ?? 1;
  });
  isInvoice = computed(() => {
    return this.billQueryResponse.data()?.bill?.payStatusId === 1;
  });
  @Input() amount;
  @Input() status;
  billReference = input<string>();

  ref = signal('');

  isLoading = computed(() => {
    return (
      (this.billQueryResponse.fetchStatus() === 'fetching' &&
        this.billQueryResponse.isPending()) ||
      (this.billTypeQueryResponse.fetchStatus() === 'fetching' &&
        this.billTypeQueryResponse.isPending())
    );
  });

  billType = computed(() => {
    return this.billTypeQueryResponse.data() || 'Unknown';
  });

  billTypeQueryResponse = injectQuery(() => {
    return {
      queryKey: ['get-bill-type', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billReference] = queryKey;
        const billRef = ref || billReference;
        return this.billService.getBillType({
          billReference: billRef,
        });
      },
      enabled: !!this.ref() || !!this.billReference(),
      retry: 1,
    };
  });

  demandNoticebillQueryResponse = injectQuery(() => {
    return {
      queryKey: ['demand-notice-single-bill', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getDemandNoticeBill({
          billReference: ref || billRef,
        });
      },
      enabled:
        (!!this.ref() || !!this.billReference()) && this.billType() === 'DN',
      retry: 1,
    };
  });

  specialbillQueryResponse = injectQuery(() => {
    return {
      queryKey: ['special-single-bill', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getSpecialBill({
          billReference: ref || billRef,
        });
      },
      enabled:
        (!!this.ref() || !!this.billReference()) &&
        this.billType() === 'Special',
      retry: 1,
    };
  });

  billQueryResponse = injectQuery(() => {
    return {
      queryKey: ['single-bill', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getBill({
          billReference: ref || billRef,
        });
      },
      enabled: !!this.ref() || !!this.billReference(),
      retry: 1,
    };
  });

  isTsp = computed(() => {
    return this.billQueryResponse.data()?.isFromTsp;
  });

  receiptTpui = computed(() => {
    return this.billQueryResponse.data()?.userData?.userRegistration
      ?.addressLine1;
  });

  receiptData = computed<ReceiptData>(() => {
    const bill = this.billQueryResponse.data() as ObjectLiteral;
    const userData = this.billQueryResponse.data()?.userData?.user;
    const userAddress =
      this.billQueryResponse.data()?.userData?.userRegistration?.addressLine1;
    return {
      ...bill,
      billTo: {
        name: `${userData?.firstName} ${userData?.lastName}`,
        address: userAddress || '',
        email: `${userData?.email}`,
      },
      billFrom: {
        name: 'Kaduna State Internal Revenue Service',
        address: 'Kaduna State',
      },
      items: (bill?.bill?.billDetails || []).map((item) => ({
        id: item?.id,
        amount: item?.amount || 0,
        rate: item?.amount || 0,
        description: item?.description || '',
        quantity: 1,
        narration: item?.narration || '',
      })),
      totalAmount:
        (bill?.bill?.billDetails || []).reduce((a, b) => a + b.amount, 0) || 0,
      userData: this.billQueryResponse.data()?.userData || {},
    } as ReceiptData;
  });

  get issuanceDate() {
    return dayjs().format('MMM DD, YYYY');
  }
  get expiryDate() {
    return dayjs().add(3, 'months').format('MMM DD, YYYY');
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((paramsMap) => {
        this.ref.set(
          paramsMap.get('billRef') ||
            paramsMap.get('billReference') ||
            paramsMap.get('ref'),
        );
      });
  }
}
